import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from './api'
import type { User } from './types'
import {
  clearAllTokens,
  clearImpersonationToken,
  getEffectiveToken,
  getImpersonationToken,
  setImpersonationToken,
  setSessionToken,
} from './storage'

export interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isImpersonating: boolean
  login: (token: string) => Promise<void>
  logout: () => void
  startImpersonation: (token: string) => Promise<void>
  stopImpersonation: () => Promise<void>
  refresh: () => Promise<void>
}

// Exported so scripts/prerender.mjs (via entry-server.tsx) can supply a
// static, no-fetch stub value for server rendering the public marketing
// pages -- MarketingHeader calls useAuth(), which throws without a
// Provider in scope, and the real AuthProvider's effect-driven /auth/me
// fetch has no server/session context to run against at build time anyway.
export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isImpersonating, setIsImpersonating] = useState(() => getImpersonationToken() !== null)

  async function refresh() {
    // No token anywhere (a fresh/logged-out visitor on a public marketing
    // page) -- skip the request entirely rather than firing one that can
    // only ever come back a 403. Every caller that actually has a token
    // (login/startImpersonation/stopImpersonation) sets it immediately
    // before calling refresh(), so this never skips a call that should
    // have gone out. Found via a PageSpeed audit (2026-10-09): Chrome
    // logs any non-2xx fetch as a console error regardless of how
    // "expected" the app itself treats it, which was costing a Best
    // Practices point on every anonymous page load for no real benefit.
    if (!getEffectiveToken()) {
      setUser(null)
      setIsLoading(false)
      return
    }
    try {
      const me = await api.get<User>('/api/v1/auth/me')
      setUser(me)
      setIsImpersonating(getImpersonationToken() !== null)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function login(token: string) {
    setSessionToken(token)
    await refresh()
  }

  function logout() {
    clearAllTokens()
    setUser(null)
    setIsImpersonating(false)
    window.location.assign('/login')
  }

  async function startImpersonation(token: string) {
    setImpersonationToken(token)
    await refresh()
  }

  async function stopImpersonation() {
    clearImpersonationToken()
    await refresh()
    window.location.assign('/admin')
  }

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isImpersonating, login, logout, startImpersonation, stopImpersonation, refresh }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
